import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format } from "date-fns";
import Plot from "react-plotly.js";
import { toast } from "react-toastify";

import { supportGetLimits } from "src/apiService/modules/support";
import type { PlatformLimitsSample } from "src/interfaces/platformLimits.interface";
import SupportDateRangeFilter from "src/pages/support/SupportDateRangeFilter";
import {
  DayMs,
  rangeForSupportDatePreset,
  type SupportDateRangePresetId,
} from "src/pages/support/dateRangePresets";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";

const PLATFORM = "etsy";

const SERIES = [
  { key: "limitPerDay" as const, name: "Limit per day", color: "#1f77b4" },
  {
    key: "limitPerSecond" as const,
    name: "Limit per second",
    color: "#ff7f0e",
  },
  {
    key: "remainingThisSecond" as const,
    name: "Remaining this second",
    color: "#2ca02c",
  },
  {
    key: "remainingToday" as const,
    name: "Remaining today",
    color: "#d62728",
  },
];

const CHART_SERIES = [
  {
    key: "remainingThisSecond" as const,
    name: "Remaining this second",
    color: "#2ca02c",
    yaxis: "y" as const,
  },
  {
    key: "remainingToday" as const,
    name: "Remaining today",
    color: "#d62728",
    yaxis: "y2" as const,
  },
];

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  latestGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  latestItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  chartWrapper: {
    width: "100%",
  },
  chart: {
    width: "100%",
    minHeight: 420,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

function formatReportedDate(date: string | null) {
  if (!date) return "Unknown";
  return format(new Date(date), "MMM d, yyyy hh:mm:ss a");
}

function buildChartTraces(samples: PlatformLimitsSample[]) {
  const x = samples.map((sample) =>
    sample.date ? new Date(sample.date) : null
  );

  return CHART_SERIES.map((series) => ({
    x,
    y: samples.map((sample) => sample[series.key]),
    name: series.name,
    type: "scatter" as const,
    mode: "lines" as const,
    yaxis: series.yaxis,
    line: { color: series.color, width: 2 },
    hovertemplate: `%{fullData.name}<br />%{x}<br />%{y:,}<extra></extra>`,
  }));
}

function EtsyLimits() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);

  const [loading, setLoading] = useState(false);
  const [latest, setLatest] = useState<PlatformLimitsSample | null>(null);
  const [samples, setSamples] = useState<PlatformLimitsSample[]>([]);
  const [activePreset, setActivePreset] =
    useState<SupportDateRangePresetId | null>("1d");
  const [from, setFrom] = useState(() => new Date(Date.now() - DayMs));
  const [to, setTo] = useState(() => new Date());

  const fetchLimits = useCallback(
    async (fromDate: Date, toDate: Date) => {
      if (!token) return;

      setLoading(true);
      try {
        const { data } = await supportGetLimits(token, {
          platform: PLATFORM,
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
        });
        setLatest(data.latest);
        setSamples(data.samples || []);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        setLatest(null);
        setSamples([]);
        toast.error(err?.message || "Failed to load Etsy limits");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, token]
  );

  const applyPreset = useCallback(
    (presetId: SupportDateRangePresetId, fromDate: Date, toDate: Date) => {
      setActivePreset(presetId);
      setFrom(fromDate);
      setTo(toDate);
      void fetchLimits(fromDate, toDate);
    },
    [fetchLimits]
  );

  useEffect(() => {
    if (!token) return;
    const range = rangeForSupportDatePreset("1d");
    applyPreset("1d", range.from, range.to);
  }, [token, applyPreset]);

  const plot = useMemo(() => {
    if (!samples.length) return null;

    const traces = buildChartTraces(samples);
    const denseXLabels = samples.length > 12;

    return (
      <Box className={classes.chartWrapper}>
        <Plot
          className={classes.chart}
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
          data={traces}
          revision={samples.length}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 420,
            width: undefined,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            margin: {
              l: 64,
              r: 64,
              t: 56,
              b: denseXLabels ? 88 : 64,
            },
            xaxis: {
              title: "Time",
              tickangle: denseXLabels ? -40 : 0,
              automargin: true,
              nticks: denseXLabels ? Math.min(12, samples.length) : undefined,
            },
            yaxis: {
              title: "Remaining this second",
              titlefont: { color: "#2ca02c" },
              tickfont: { color: "#2ca02c" },
              automargin: true,
              side: "left",
            },
            yaxis2: {
              title: "Remaining today",
              titlefont: { color: "#d62728" },
              tickfont: { color: "#d62728" },
              automargin: true,
              overlaying: "y",
              side: "right",
            },
            legend: {
              orientation: "h",
              yanchor: "bottom",
              y: 1.02,
              x: 0,
              xanchor: "left",
            },
            showlegend: true,
          }}
        />
      </Box>
    );
  }, [
    samples,
    classes.chart,
    classes.chartWrapper,
    theme.palette.text.primary,
  ]);

  return (
    <Box component={Paper} className={classes.paper}>
      <Box className={classes.header}>
        <Box className={classes.headerText}>
          <Typography variant="h6" gutterBottom>
            Etsy Limits
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Rate-limit headers captured from Etsy API responses.
          </Typography>
        </Box>
        <IconButton
          aria-label="Refresh Etsy limits"
          color="primary"
          disabled={loading}
          onClick={() => {
            if (activePreset) {
              const range = rangeForSupportDatePreset(activePreset);
              applyPreset(activePreset, range.from, range.to);
            } else {
              void fetchLimits(from, to);
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Box className={classes.latestGrid}>
        <Box className={classes.latestItem}>
          <Typography variant="caption" color="textSecondary">
            Reported at
          </Typography>
          <Typography variant="body1">
            {latest ? formatReportedDate(latest.date) : "—"}
          </Typography>
        </Box>
        {SERIES.map((series) => (
          <Box key={series.key} className={classes.latestItem}>
            <Typography variant="caption" color="textSecondary">
              {series.name}
            </Typography>
            <Typography variant="body1">
              {latest != null ? latest[series.key].toLocaleString() : "—"}
            </Typography>
          </Box>
        ))}
      </Box>

      <SupportDateRangeFilter
        from={from}
        to={to}
        activePreset={activePreset}
        onPresetChange={applyPreset}
        onFromChange={setFrom}
        onToChange={setTo}
        onApply={() => {
          setActivePreset(null);
          void fetchLimits(from, to);
        }}
      />

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : plot ? (
        plot
      ) : (
        <Typography color="textSecondary">
          No Etsy limit samples in this range.
        </Typography>
      )}
    </Box>
  );
}

export default EtsyLimits;
